import { envConfig } from '@headless-workspace/config';
import Redis, { RedisOptions } from 'ioredis';
import { IsomorphicLogger } from '../logger';

const logger = new IsomorphicLogger();

export const LOGGER_LABEL = 'LoggerCachedRepository';

export const CONNECTION_NAME = 'storefront-rendering-engine';

export const REDIS_NOT_CONFIGURED_MSG = '🔄 Redis is not configured';
export const REDIS_CONNECTING_MSG = '🔄 Starting Redis connection';
export const REDIS_CONNECTED_MSG = '✅ Redis connected';
export const REDIS_READY_MSG = '🚀 Redis ready';
export const REDIS_INIT_FAILED_MSG = '❌ Redis initialization failed';
export const REDIS_AUTH_FAILED_MSG = '❌ Redis authentication failed';
const REDIS_NOT_READY_ERROR = '❌ Redis not ready';

const MAX_RETRY_CONNECT_DELAY = 2000;
const RETRY_CONNECT_INTERVAL = 100;

const REDIS_OPTIONS: RedisOptions = {
    connectionName: CONNECTION_NAME,
    lazyConnect: true,
    enableReadyCheck: true,
    maxRetriesPerRequest: envConfig.redisConnectMaxRetries,
};

const REDIS_FAILURE_ERRORS = ['invalid username-password', 'NOAUTH', 'WRONGPASS'];

let redis: Redis | null = null;

// TODO: Add Redis implementation in docker container for local dev
export async function initRedis(): Promise<Redis | null> {
    // Disable Redis when it's not configured
    if (!envConfig.redisUrl) {
        logger.info(LOGGER_LABEL, REDIS_NOT_CONFIGURED_MSG);
        return null;
    }

    if (redis) {
        // Return the existing Redis instance if it's already initialized'
        return redis;
    }

    try {
        let retryCount = 0;

        const redisOptions: RedisOptions = {
            ...REDIS_OPTIONS,
            password: envConfig.redisPassword,
            // Disable infinite retries
            retryStrategy(times) {
                retryCount += 1;

                if (retryCount > envConfig.redisConnectMaxRetries) {
                    return null; // Stop reconnecting
                }

                // Retry after N ms
                return Math.min(times * RETRY_CONNECT_INTERVAL, MAX_RETRY_CONNECT_DELAY);
            },
        };

        logger.info(LOGGER_LABEL, REDIS_CONNECTING_MSG);

        const redisInstance = new Redis(envConfig.redisUrl, redisOptions);

        redisInstance.on('error', (err) => {
            if (REDIS_FAILURE_ERRORS.find((failureError) => err.message.includes(failureError))) {
                logger.error(LOGGER_LABEL, REDIS_AUTH_FAILED_MSG, err.message);
                redisInstance?.disconnect();
                redis = null; // Clean up on authentication errors
            }
        });

        await redisInstance.connect();

        logger.info(LOGGER_LABEL, REDIS_CONNECTED_MSG);

        const isReady = redisInstance.status === 'ready';

        if (!isReady) {
            // If Redis is not ready, clean up and return null
            logger.error(LOGGER_LABEL, REDIS_NOT_READY_ERROR);
            redis = null;
        } else {
            // If Redis is ready, check if communication is operational
            const pong = await redisInstance.ping();
            if (pong === 'PONG') {
                logger.info(LOGGER_LABEL, REDIS_READY_MSG);
                redis = redisInstance;
            } else {
                // Log error if ping response is not PONG
                logger.error(LOGGER_LABEL, REDIS_INIT_FAILED_MSG);
            }
        }
    } catch (error) {
        logger.error(LOGGER_LABEL, `${REDIS_INIT_FAILED_MSG}: ${error}`);
        redis = null; // Clean up if an uncaught error occurs
    }

    return redis;
}
